import * as Yup from "yup";
import countryList from "react-select-country-list";
import { i18n } from "../../../private/languageSelector";

// Same fields, order and rules as the backend (server/helpers/store-address.js)
export const ADDRESS_FIELDS = ["buildingNumber", "streetName", "district", "city", "postalCode", "country"];
export const MAX_FIELD_LENGTH = 250;
export const SAUDI_ARABIA = "SA";

const SAUDI_ALIASES = ["sa", "sau", "ksa", "saudi arabia", "kingdom of saudi arabia", "the kingdom of saudi arabia", "السعودية", "المملكة العربية السعودية"];

// Saudi National Address: 4-digit building number, 5-digit postal code (ZATCA BR-KSA-37 / BR-KSA-66)
const SAUDI_BUILDING_NUMBER = /^\d{4}$/;
const SAUDI_POSTAL_CODE = /^\d{5}$/;

export const emptyAddress = () => ({ buildingNumber: "", streetName: "", district: "", city: "", postalCode: "", country: "" });

// ISO 3166-1 alpha-2 code; spellings of Saudi Arabia become "SA"
export function normalizeCountry(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (SAUDI_ALIASES.includes(trimmed.toLowerCase())) return SAUDI_ARABIA;
  if (/^[a-z]{2}$/i.test(trimmed)) return trimmed.toUpperCase();
  return trimmed;
}

export const isSaudiArabia = (country) => normalizeCountry(country) === SAUDI_ARABIA;

export const isEmptyAddress = (details) => !details || ADDRESS_FIELDS.every((field) => !(details[field] || "").trim());

// Popup values from a saved address (missing / null parts become "")
export function toAddressForm(details) {
  const form = emptyAddress();
  if (details && typeof details === "object") {
    ADDRESS_FIELDS.forEach((field) => {
      if (typeof details[field] === "string") form[field] = details[field];
    });
  }
  form.country = normalizeCountry(form.country);
  return form;
}

// What the popup saves: trimmed values, nothing else changed
export function cleanAddress(values) {
  const cleaned = emptyAddress();
  ADDRESS_FIELDS.forEach((field) => {
    cleaned[field] = typeof values[field] === "string" ? values[field].trim() : "";
  });
  cleaned.country = normalizeCountry(cleaned.country);
  return cleaned;
}

let displayNames = {};
export function getCountryName(code, locale = "en") {
  if (!code) return "";
  try {
    if (typeof Intl !== "undefined" && Intl.DisplayNames) {
      displayNames[locale] = displayNames[locale] || new Intl.DisplayNames([locale], { type: "region" });
      const name = displayNames[locale].of(code);
      if (name && name !== code) return name;
    }
  } catch (e) {
    // fall through to the country list
  }
  return countryList().getLabel(code) || code;
}

// Country dropdown options, labelled in the UI language and sorted by that label
export function getCountryOptions(language) {
  const locale = language === "arabic" ? "ar" : "en";
  return countryList()
    .getData()
    .map(({ value }) => ({ value, label: getCountryName(value, locale) }))
    .sort((a, b) => a.label.localeCompare(b.label, locale));
}

/**
 * Single-line address: building number, street, district, city, postal code, country name.
 * Empty parts are left out and a repeated part is shown once. Matches the backend, which stores
 * this string as store.address.
 */
export function formatStoreAddress(details) {
  if (isEmptyAddress(details)) return "";
  const parts = [
    details.buildingNumber,
    details.streetName,
    details.district,
    details.city,
    details.postalCode,
    getCountryName(normalizeCountry(details.country)),
  ];
  const seen = new Set();
  return parts
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter((part) => {
      if (!part || seen.has(part.toLowerCase())) return false;
      seen.add(part.toLowerCase());
      return true;
    })
    .join(", ");
}

// Text shown in the Store Address field: the structured address, else a legacy free-text address
export const getStoreAddressDisplay = (details, legacyAddress) =>
  !isEmptyAddress(details) ? formatStoreAddress(details) : typeof legacyAddress === "string" ? legacyAddress.trim() : "";

// Store form values -> create/update request body. A store with only a legacy address (no
// structured address) keeps it as is: no addressDetails is sent and the address text is unchanged.
export function withStoreAddress(values) {
  const { addressDetails, ...rest } = values;
  return addressDetails ? { ...rest, addressDetails } : rest;
}

const REQUIRED_MESSAGE_KEYS = {
  buildingNumber: "StoreAddress.BuildingNumberRequired",
  streetName: "StoreAddress.StreetNameRequired",
  district: "StoreAddress.DistrictRequired",
  city: "StoreAddress.CityRequired",
  postalCode: "StoreAddress.PostalCodeRequired",
};

// Required (trimmed, so whitespace-only is empty) only when the country is Saudi Arabia
const textField = (field, format, formatMessageKey) =>
  Yup.string()
    .max(MAX_FIELD_LENGTH, () => i18n("StoreAddress.MaxLength"))
    .test(`${field}-required`, () => i18n(REQUIRED_MESSAGE_KEYS[field]), function(value) {
      return !isSaudiArabia(this.parent.country) || !!(value || "").trim();
    })
    .test(`${field}-format`, () => i18n(formatMessageKey || "StoreAddress.MaxLength"), function(value) {
      const trimmed = (value || "").trim();
      return !format || !isSaudiArabia(this.parent.country) || !trimmed || format.test(trimmed);
    });

export const buildStoreAddressSchema = () =>
  Yup.object().shape({
    buildingNumber: textField("buildingNumber", SAUDI_BUILDING_NUMBER, "StoreAddress.BuildingNumberFormat"),
    streetName: textField("streetName"),
    district: textField("district"),
    city: textField("city"),
    postalCode: textField("postalCode", SAUDI_POSTAL_CODE, "StoreAddress.PostalCodeFormat"),
    country: Yup.string().test("country-required", () => i18n("StoreAddress.CountryRequired"), (value) => !!(value || "").trim()),
  });
